import React, { Fragment, forwardRef } from 'react';
import { Input as AntInput } from 'antd';
import { Label } from '../../label/label.js';
import OTP from './otp.js';
import { NackleCaption } from '../../NackleCaption/nackle-caption.js';
import { Close } from '@nackle/origami-icons';
import Password from './password';
import Search from './search';

/**
 * An input for a form
 *
 * {@link https://ant.design/components/input#api}
 *
 * @module Input
 * @param {Object} props - the Input props
 * @param {boolean} [props.allowClear] - Whether or not the input can be "cleared" with a button integrated into the input
 * @param {string} [props.status] - the status of the input
 * @param {React.ReactNode} [props.statusIcon] - the icon that appears in the input
 * @param {string} [props.suffix] - optional text that appears at the end of the input
 * @param {string} [props.label] - optional label that appears before the input
 * @param {Object} [props.labelProps] - optional props for the input label
 * @param {boolean} [props.caption] - whether or not the input has a caption below
 * @param {Object} [props.captionProps] - The props for the optional caption
 * @param {boolean} [props.captionProps.required] - whether or not the caption is required
 * @param {string} [props.captionProps.text] - text for caption under input
 * @param {string} [props.captionProps.size] - size of caption
 * @returns {React.ReactElement} - a form input
*/
const Input = forwardRef( ( props, ref ) => {
    const { allowClear, status, statusIcon, suffix, label, labelProps, caption, captionProps, disabled, ...restProps } = props;

    const statusSuffix = (
        <Fragment>
            { suffix }
            { statusIcon }
        </Fragment>
    );

    return (
        label ?
            <Label text={ label } { ...labelProps } withInput={ true } size={ restProps.size }>
                <div className="input-caption-wrapper">
                    <AntInput
                        ref={ ref }
                        status={ status }
                        suffix={ status ? statusSuffix : suffix }
                        allowClear={ allowClear ? { clearIcon: <Close /> } : false }
                        disabled={ disabled }
                        { ...restProps }
                    />
                    {
                        caption &&
                        <NackleCaption { ...captionProps } state={ status } disabled={ disabled } />
                    }
                </div>
            </Label>
            :
            <div className="input-caption-wrapper">
                <AntInput
                    ref={ ref }
                    status={ status }
                    suffix={ status ? statusSuffix : suffix }
                    allowClear={ allowClear ? { clearIcon: <Close /> } : false }
                    disabled={ disabled }
                    { ...restProps }
                />
                {
                    caption &&
                    <NackleCaption { ...captionProps } size={ restProps.size } state={ status } disabled={ disabled } />
                }
            </div>
    );
} );

const TextArea = ( { label, labelProps, caption, captionProps, disabled, status, suffix, statusIcon, allowClear,  ...restProps } ) => {
    const statusSuffix = (
        <Fragment>
            { suffix }
            { statusIcon }
        </Fragment>
    );
    return (
        label ?
            <Label text={ label } { ...labelProps } withInput={ true } size={ restProps.size } >
                <div className="input-caption-wrapper">
                    <AntInput.TextArea { ...restProps } disabled={ disabled } allowClear={ allowClear ? { clearIcon: <Close /> } : false } />
                    {
                        caption &&
                        <NackleCaption { ...captionProps } status={ restProps.status } suffix={ status ? statusSuffix : suffix } disabled={ disabled } />
                    }
                </div>
            </Label>
            :
            <div className="input-caption-wrapper">
                <AntInput.TextArea { ...restProps } disabled={ disabled } allowClear={ allowClear ? { clearIcon: <Close /> } : false } />
                {
                    caption &&
                    <NackleCaption { ...captionProps } size={ restProps.size } status={ restProps.status } suffix={ status ? statusSuffix : suffix } disabled={ disabled } />
                }
            </div>

    );
};
Input.Password = Password;
Input.Search = Search;
Input.OTP = OTP;
Input.TextArea = TextArea;

export { Input };
